import React, { useCallback, useEffect, useState } from 'react';
import { FileDropArea, openErrorNotification, Text, Button, Icon } from 'hyarchis-global';
import produce from 'immer';
import TextArea from 'antd/lib/input/TextArea';
import { ProcessingJob } from 'types/proccesingJobs';
import ProcessingJobRepository from 'services/api-repositories/processingJobs/ProcessingJobRepository';
import poll from 'services/poller';
import { ExtendedFile, JobOutput, RiskAssessmentResults } from 'types';
import { useActions, useShallowSelector } from 'hooks';
import { ApplicationState } from 'store/types';
import { updateJobFiles as updateJobFilesAction } from 'store/jobs/actions';
import FileService from 'services/file-service';
import { AxiosError } from 'axios';
import styled from 'styled-components';
import { NotificationSettings } from 'hyarchis-global/components/notifications/types';
import DocumentUploadForm from '../../upload/DocumentUploadForm';
import JobList from './JobList';
import Styled from '../listItemHead.styled';

const StyledWrapper = styled.div`
  display: flex;
  height: 100%;
`;

const StyledLeftSide = styled.div`
  width: 30%;
  margin: 10px;
`;

const StyledRightSide = styled.div`
  width: 70%;
  margin: 10px;
  overflow-y: auto;
`;

const StyledSearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const StyledLabel = styled.label`
  flex: 0 0 auto;
  margin-top: 10px;
`;

const StyledTextValidatable = styled.div`
  flex: 1;
`;

const StyledSearchButton = styled(Button)`
  margin-top: 10px;
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(110vh - 200px);
  width: 100%;
  align-items: center;
`;

const StyledFileDropArea = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
`;

const { StyledTextAreaWrapper } = Styled;

const isSameFile = (file1: ExtendedFile, file2: ExtendedFile): boolean =>
  file1 && file2 && file1.addedAt === file2.addedAt && file1.name === file2.name;

const ProcessingJobsContainer = () => {
  const { stateJobFiles } = useShallowSelector(({ jobs }: ApplicationState) => ({
    stateJobFiles: jobs.jobFiles,
  }));

  const { updateJobFiles } = useActions({
    updateJobFiles: updateJobFilesAction,
  });

  const [jobOutputs, setJobOutput] = useState<JobOutput[]>(stateJobFiles);
  const [searchablePerson, setSearchablePerson] = useState<string>('');
  const [definedInputJson, setDefinedInputJson] = useState<string>('');
  const [inputFileConfigurations, setInputFileConfigurations] = useState<string>('');

  const changeSearchResults = (searchResult: string | undefined) => {
    setSearchablePerson(searchResult!);
  };

  useEffect(() => {
    const uploadedJobFiles = jobOutputs.filter((jobFile) => jobFile.job);
    updateJobFiles(uploadedJobFiles);
  }, [updateJobFiles, jobOutputs]);

  useEffect(() => {
    const inProgressJobFiles = stateJobFiles.filter(
      (jobFile) => jobFile.job && jobFile.job.status === 'inProgress',
    );
    for (const jobFile of inProgressJobFiles) {
      processJob(jobFile.job!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processJob = async (job: ProcessingJob) => {
    let finishedJob: ProcessingJob | undefined;
    let jobError: string | undefined;
    try {
      finishedJob = await poll<ProcessingJob>(
        () => ProcessingJobRepository.get(job.processingJobId),
        (fetchedJob) => fetchedJob.status === 'inProgress',
      );
    } catch (error) {
      jobError = (error as Error).message;
    }
    setJobOutput((existingJobs) =>
      produce(existingJobs, (jobsDraft) => {
        const jobDraft = jobsDraft.find(
          (j) => j.job && j.job.processingJobId === job.processingJobId,
        )!;
        if (finishedJob) {
          jobDraft.job = finishedJob;
        }

        if (jobError) {
          jobDraft.error = jobError;
        }
      }),
    );
  };

  const handleAddFiles = useCallback(
    async (newFiles: File[]) => {
      const parsedFiles = await FileService.parseMultipleFiles(newFiles);
      const newJobFiles = parsedFiles.map<JobOutput>((newFile) => ({
        file: newFile,
        riskAssessmentResults: undefined,
        skipProcessing: false,
        documentId: undefined,
      }));
      setJobOutput((existingJobFiles) => [...existingJobFiles, ...newJobFiles]);
      for (const parsedFile of parsedFiles) {
        ProcessingJobRepository.create(
          parsedFile.originalFile,
          definedInputJson,
          inputFileConfigurations,
        ).then(
          async (job) => {
            setJobOutput((existingJobFiles) =>
              produce(existingJobFiles, (jobFileDrafts) => {
                // eslint-disable-next-line no-param-reassign
                jobFileDrafts.find((jF) => isSameFile(jF.file!, parsedFile))!.job = job;
              }),
            );
            await processJob(job);
          },
          (error) => {
            openErrorNotification((error as AxiosError).message);
            setJobOutput((existingJobFiles) =>
              existingJobFiles.filter((jF) => !isSameFile(jF.file!, parsedFile)),
            );
          },
        );
      }
    },
    [definedInputJson, inputFileConfigurations],
  );
  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDefinedInputJson(event.target.value);
  };

  const handleInputFileConfigurationsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputFileConfigurations(event.target.value);
  };

  const handleRiskAssessmentSearch = useCallback(async (searchResult?: string) => {
    if (!searchResult) {
      const notificationSettings: NotificationSettings = {
        duration: 3,
      };
      const errorMessage = 'Search result must be provided';
      openErrorNotification(errorMessage, notificationSettings);
      return;
    }

    const searchStartDate = new Date();
    const newRiskAssessmentModel: RiskAssessmentResults = {
      personName: searchResult!,
      addedAt: searchStartDate,
    };

    const files: JobOutput = {
      job: undefined,
      error: undefined,
      file: undefined,
      riskAssessmentResults: newRiskAssessmentModel,
      skipProcessing: false,
      documentId: undefined,
    };
    const newRiskAssessments = [files];
    setJobOutput((existingRiskAssessments) => [...existingRiskAssessments, ...newRiskAssessments]);
    try {
      const job = await ProcessingJobRepository.createSearch(searchResult);
      setJobOutput((existingJobFiles) =>
        produce(existingJobFiles, (jobFileDrafts) => {
          // eslint-disable-next-line no-param-reassign
          jobFileDrafts.find(
            (jF) =>
              jF.riskAssessmentResults?.personName === searchResult &&
              jF.riskAssessmentResults?.addedAt === searchStartDate,
          )!.job = job;
        }),
      );
      await processJob(job);
      // eslint-disable-next-line no-empty
    } catch (error) {}
  }, []);

  const handleRemove = useCallback((processingJobId: string) => {
    setJobOutput((existingJobFiles) =>
      existingJobFiles.filter((jf) => jf.job && jf.job.processingJobId !== processingJobId),
    );
  }, []);

  const handleRefresh = useCallback(async (processingJobId: string) => {
    setJobOutput((existingJobs) =>
      produce(existingJobs, (jobsDraft) => {
        // eslint-disable-next-line no-param-reassign
        jobsDraft.find((j) => j.job && j.job.processingJobId === processingJobId)!.error =
          undefined;
      }),
    );
    let refreshedJob: ProcessingJob;
    let errorMessage: string | undefined;
    try {
      refreshedJob = await ProcessingJobRepository.get(processingJobId);
      if (refreshedJob.status === 'inProgress') {
        errorMessage = 'Job is still in progress. Try later.';
      }
    } catch (error) {
      errorMessage = (error as AxiosError).message;
      openErrorNotification(errorMessage);
    }
    setJobOutput((existingJobs) =>
      produce(existingJobs, (jobsDraft) => {
        const jobDraft = jobsDraft.find((j) => j.job && j.job.processingJobId === processingJobId)!;
        if (refreshedJob) {
          jobDraft.job = refreshedJob;
        }
        jobDraft.error = errorMessage;
      }),
    );
  }, []);

  return (
    <StyledWrapper>
      <StyledLeftSide>
        <StyledSearchWrapper>
          <StyledLabel>Search person risk level:</StyledLabel>
          <StyledTextValidatable>
            <Text.Validatable
              value={searchablePerson}
              onChange={(v) => changeSearchResults(v?.toString())}
            />
          </StyledTextValidatable>
          <StyledSearchButton
            type='default'
            onClick={() => handleRiskAssessmentSearch(searchablePerson)}
            icon={<Icon type='regular' icon='search' />}
          />
        </StyledSearchWrapper>
        <Container>
          <StyledSearchWrapper>
            <StyledTextAreaWrapper>
              <StyledLabel>Defined input json:</StyledLabel>
              <TextArea value={definedInputJson} onChange={handleChange} />
            </StyledTextAreaWrapper>
          </StyledSearchWrapper>
          <StyledSearchWrapper>
            <StyledTextAreaWrapper>
              <StyledLabel>File configurations:</StyledLabel>
              <TextArea
                value={inputFileConfigurations}
                onChange={handleInputFileConfigurationsChange}
              />
            </StyledTextAreaWrapper>
          </StyledSearchWrapper>
          <StyledFileDropArea>
            <FileDropArea addFiles={handleAddFiles} draggingStyle={{ opacity: 1 }}>
              <DocumentUploadForm allowMultiple addFiles={handleAddFiles} />
            </FileDropArea>
          </StyledFileDropArea>
        </Container>
      </StyledLeftSide>
      <StyledRightSide>
        <JobList
          jobFiles={jobOutputs}
          remove={handleRemove}
          jobCrossMatches={[]}
          refresh={handleRefresh}
        />
      </StyledRightSide>
    </StyledWrapper>
  );
};

export default React.memo(ProcessingJobsContainer);
